import React from 'react';
import { MagazineSection } from './MagazineSection';
import { StatsSection } from './StatsSection';
import { GlobeSection } from './GlobeSection';
import { AccordionLinksSection } from './AccordionLinksSection';
import { FooterBottomBar } from './FooterBottomBar';
import type { FooterV2Props } from './types';

/**
 * Error boundary wrapper for FooterV2 to prevent crashes from breaking the page.
 */
class FooterErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback: React.ReactNode },
    { hasError: boolean }
> {
    constructor(props: {
        children: React.ReactNode;
        fallback: React.ReactNode;
    }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('FooterV2 error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}

/**
 * New footer design (v2) for desktop.
 * Features:
 * - Magazine showcase section with 3 recent digital magazines
 * - Stats section with hardcoded metrics
 * - Globe section with ArchiPro logo
 * - Accordion-style navigation links
 * - Bottom bar with copyright and legal links
 */
export const FooterV2: React.FC<FooterV2Props> = ({
    magazines,
    homeownersLinks,
    businessLinks,
    supportLinks,
    websiteTermLink,
    privacyPolicyLink,
    currentCountryCode,
    onNavClick,
    onSignUpClick,
    subscriptionAction,
    subscriptionFetcher,
    videoContent,
    magazineFallbackImageSrc,
    globeFallbackImageSrc,
    isLoggedIn,
}) => {
    // Only pass video data if videos are enabled
    const magazineVideoData = videoContent?.enabled
        ? videoContent.magazineVideo
        : null;
    const globeVideoData = videoContent?.enabled
        ? videoContent.globeVideo
        : null;

    return (
        <FooterErrorBoundary fallback={null}>
            <footer className="bg-[#212121]">
                <MagazineSection
                    magazines={magazines}
                    subscriptionAction={subscriptionAction}
                    subscriptionFetcher={subscriptionFetcher}
                    videoData={magazineVideoData}
                    fallbackImageSrc={magazineFallbackImageSrc}
                />
                <StatsSection />
                <GlobeSection
                    onSignUpClick={onSignUpClick}
                    videoData={globeVideoData}
                    fallbackImageSrc={globeFallbackImageSrc}
                    isLoggedIn={isLoggedIn}
                />
                <AccordionLinksSection
                    homeownersLinks={homeownersLinks}
                    businessLinks={businessLinks}
                    supportLinks={supportLinks}
                    defaultOpenSection={
                        currentCountryCode === 'US' ? 'support' : 'homeowners'
                    }
                    onNavClick={onNavClick}
                />
                <FooterBottomBar
                    websiteTermLink={websiteTermLink}
                    privacyPolicyLink={privacyPolicyLink}
                    currentCountryCode={currentCountryCode}
                    onNavClick={onNavClick}
                />
            </footer>
        </FooterErrorBoundary>
    );
};
